import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from 'recharts';
import {
  Cog, Wrench, Truck, AlertTriangle, Clock, Package,
  Shield, ChevronRight, RefreshCw, TicketCheck, TrendingUp, Activity,
  Battery, Trophy, Bell, AlertOctagon, ClipboardList
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];
const PRIORITY_COLORS = { critical: '#dc2626', high: '#f97316', medium: '#eab308', low: '#94a3b8', 'N/A': '#cbd5e1' };

const AnalyticsDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wmsWidgets, setWmsWidgets] = useState({ stockAlerts: null, batteryAlerts: null, leaderboard: null });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/analytics/main-dashboard');
      setData(res.data);
      // Fetch WMS widgets in parallel (non-blocking)
      Promise.allSettled([
        api.get('/stock-alerts'),
        api.get('/battery-service/alerts'),
        api.get('/leaderboard?days=30')
      ]).then(([sa, ba, lb]) => {
        setWmsWidgets({
          stockAlerts: sa.status === 'fulfilled' ? sa.value.data : null,
          batteryAlerts: ba.status === 'fulfilled' ? ba.value.data : null,
          leaderboard: lb.status === 'fulfilled' ? lb.value.data : null,
        });
      });
    } catch (error) {
      toast.error('Eroare la incarcarea dashboard-ului');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDataQuiet = useCallback(async () => {
    try {
      const res = await api.get('/analytics/main-dashboard');
      setData(res.data);
    } catch {}
  }, []);

  // Real-time: refresh dashboard on any significant event
  useRealtimeEvent('new_ticket', fetchDataQuiet);
  useRealtimeEvent('ticket_update', fetchDataQuiet);
  useRealtimeEvent('stock_change', fetchDataQuiet);
  useRealtimeEvent('pm_change', fetchDataQuiet);
  useRealtimeEvent('material_request', fetchDataQuiet);
  useRealtimeEvent('dashboard_refresh', fetchDataQuiet);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds as safety net
  useEffect(() => {
    const interval = setInterval(fetchDataQuiet, 30000);
    return () => clearInterval(interval);
  }, [fetchDataQuiet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm text-slate-400">Se incarca...</p>
        </div>
      </div>
    );
  }

  const c = data?.counts || {};
  const charts = data?.charts || {};

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">{p.value}</span></p>
        ))}
      </div>
    );
  };

  const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
    if (value === 0) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="600">
        {value}
      </text>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-6 overflow-x-hidden" data-testid="main-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-4xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Vedere generala</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Reincarca
        </Button>
      </div>

      {/* KPI Cards - Modern glassmorphism style */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border-0 bg-gradient-to-br from-blue-50 to-blue-100/50" onClick={() => navigate('/settings/equipment')} data-testid="kpi-equipment">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl lg:text-3xl font-extrabold text-blue-700">{c.equipment || 0}</p>
                <p className="text-[10px] lg:text-xs font-medium text-blue-500 mt-0.5 uppercase tracking-wide">Echipamente</p>
              </div>
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-blue-200/50 flex items-center justify-center">
                <Cog className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border-0 bg-gradient-to-br from-emerald-50 to-emerald-100/50" onClick={() => navigate('/tools')} data-testid="kpi-tools">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl lg:text-3xl font-extrabold text-emerald-700">{c.tools || 0}</p>
                <p className="text-[10px] lg:text-xs font-medium text-emerald-500 mt-0.5 uppercase tracking-wide">Scule active</p>
              </div>
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-emerald-200/50 flex items-center justify-center">
                <Wrench className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border-0 ${c.open_tickets > 0 ? 'bg-gradient-to-br from-red-50 to-red-100/50 ring-1 ring-red-200/50' : 'bg-gradient-to-br from-slate-50 to-slate-100/50'}`} onClick={() => navigate('/tickets')} data-testid="kpi-tickets">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl lg:text-3xl font-extrabold ${c.open_tickets > 0 ? 'text-red-600' : 'text-slate-700'}`}>{c.open_tickets || 0}</p>
                <p className={`text-[10px] lg:text-xs font-medium mt-0.5 uppercase tracking-wide ${c.open_tickets > 0 ? 'text-red-500' : 'text-slate-500'}`}>Tichete deschise</p>
                {c.tickets_today > 0 && <p className="text-[10px] text-red-400 mt-0.5 font-semibold">+{c.tickets_today} azi</p>}
              </div>
              <div className={`w-9 h-9 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl flex items-center justify-center ${c.open_tickets > 0 ? 'bg-red-200/50' : 'bg-slate-200/50'}`}>
                <TicketCheck className={`w-5 h-5 lg:w-6 lg:h-6 ${c.open_tickets > 0 ? 'text-red-600' : 'text-slate-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border-0 bg-gradient-to-br from-violet-50 to-violet-100/50" onClick={() => navigate('/iscir')} data-testid="kpi-iscir-equipment">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl lg:text-3xl font-extrabold text-violet-700">{c.iscir_equipment || 0}</p>
                <p className="text-[10px] lg:text-xs font-medium text-violet-500 mt-0.5 uppercase tracking-wide">Echipamente ISCIR</p>
              </div>
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-violet-200/50 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 lg:w-6 lg:h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Availability KPI */}
        <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border-0 bg-gradient-to-br from-cyan-50 to-cyan-100/50 col-span-2 lg:col-span-1" data-testid="kpi-availability">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl lg:text-3xl font-extrabold text-cyan-700">{c.availability_pct || 100}%</p>
                <p className="text-[10px] lg:text-xs font-medium text-cyan-500 mt-0.5 uppercase tracking-wide">Disponibilitate</p>
                {c.eq_with_issues > 0 && <p className="text-[10px] text-cyan-400 mt-0.5">{c.eq_with_issues} cu probleme</p>}
              </div>
              <div className="w-9 h-9 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-cyan-200/50 flex items-center justify-center">
                <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Strips */}
      {(c.iscir_expired > 0 || c.iscir_expiring > 0 || c.low_stock_items > 0) && (
        <div className="space-y-2">
          {c.iscir_expired > 0 && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-red-100 transition-colors" onClick={() => navigate('/iscir')} data-testid="alert-iscir-expired">
              <Shield className="w-5 h-5 text-red-600 shrink-0" />
              <span className="text-sm font-medium text-red-800">{c.iscir_expired} autorizatii ISCIR expirate</span>
              <ChevronRight className="w-4 h-4 text-red-400 ml-auto" />
            </div>
          )}
          {c.iscir_expiring > 0 && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => navigate('/iscir')} data-testid="alert-iscir-expiring">
              <Shield className="w-5 h-5 text-amber-600 shrink-0" />
              <span className="text-sm font-medium text-amber-800">{c.iscir_expiring} autorizatii ISCIR expira in 90 zile</span>
              <ChevronRight className="w-4 h-4 text-amber-400 ml-auto" />
            </div>
          )}
          {c.low_stock_items > 0 && (
            <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => navigate('/settings/consumables')} data-testid="alert-low-stock">
              <Package className="w-5 h-5 text-orange-600 shrink-0" />
              <span className="text-sm font-medium text-orange-800">{c.low_stock_items} consumabile sub stoc minim</span>
              <ChevronRight className="w-4 h-4 text-orange-400 ml-auto" />
            </div>
          )}
        </div>
      )}

      {/* WMS Quick Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4" data-testid="wms-widgets">
        {/* Stock Alerts Widget */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-red-50/50 to-white" onClick={() => navigate('/wms/stock-alerts')} data-testid="widget-stock-alerts">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4 lg:w-5 lg:h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-700">Alerte Stoc</h3>
                <p className="text-[10px] text-slate-400">Pozitii sub stocul minim</p>
              </div>
              {wmsWidgets.stockAlerts ? (
                <div className="flex items-center gap-2 shrink-0">
                  <p className={`text-xl lg:text-2xl font-bold ${wmsWidgets.stockAlerts.total > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {wmsWidgets.stockAlerts.total}
                  </p>
                  {wmsWidgets.stockAlerts.total === 0 && <span className="text-[10px] text-emerald-500 font-medium">OK</span>}
                </div>
              ) : null}
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Battery Service Widget */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-cyan-50/50 to-white" onClick={() => navigate('/maintenance/battery-service')} data-testid="widget-battery">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0">
                <Battery className="w-4 h-4 lg:w-5 lg:h-5 text-cyan-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-700">Service Baterii</h3>
                <p className="text-[10px] text-slate-400">Lize electrice & stivuitoare</p>
              </div>
              {wmsWidgets.batteryAlerts ? (
                <div className="flex items-center gap-2 shrink-0">
                  <p className={`text-xl lg:text-2xl font-bold ${wmsWidgets.batteryAlerts.overdue > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {wmsWidgets.batteryAlerts.total_needing_attention}
                  </p>
                  {wmsWidgets.batteryAlerts.overdue > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">{wmsWidgets.batteryAlerts.overdue} intarzi.</Badge>
                  )}
                </div>
              ) : null}
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard Widget */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer bg-gradient-to-br from-amber-50/50 to-white" onClick={() => navigate('/wms/leaderboard')} data-testid="widget-leaderboard">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-3 mb-2 lg:mb-3">
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <Trophy className="w-4 h-4 lg:w-5 lg:h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-700">Clasament</h3>
                <p className="text-[10px] text-slate-400">Eficienta 30 zile</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </div>
            {wmsWidgets.leaderboard?.leaderboard?.length > 0 ? (
              <div className="space-y-1.5">
                {wmsWidgets.leaderboard.leaderboard.slice(0, 3).map((u, i) => (
                  <div key={u.name} className="flex items-center gap-2 text-xs">
                    <span className="w-5 text-center">{['🥇','🥈','🥉'][i]}</span>
                    <span className="flex-1 truncate text-slate-700 font-medium">{u.name}</span>
                    <span className="font-bold text-indigo-600">{u.score}pt</span>
                  </div>
                ))}
              </div>
            ) : <div className="h-8 flex items-center text-xs text-slate-400">Se incarca...</div>}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Tickets Last 7 Days */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2 px-3 lg:px-6">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Tichete ultimele 7 zile
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 lg:px-6">
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={charts.tickets_by_day || []} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2 px-3 lg:px-6">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" />
              Trend lunar interventii
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 lg:px-6">
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={charts.monthly_trend || []}>
                <defs>
                  <linearGradient id="gradOpen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradResolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="deschise" name="Deschise" stroke="#3b82f6" strokeWidth={2} fill="url(#gradOpen)" />
                <Area type="monotone" dataKey="rezolvate" name="Rezolvate" stroke="#10b981" strokeWidth={2} fill="url(#gradResolved)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Priority + Status Distribution */}
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2 px-3 lg:px-6">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Status tichete ({c.total_tickets || 0} total)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 lg:px-6">
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={charts.status_distribution || []}
                    cx="50%" cy="50%"
                    innerRadius={45} outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                    label={PieLabel}
                  >
                    {(charts.status_distribution || []).map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {(charts.status_distribution || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  <span className="text-[10px] text-slate-500">{item.name} ({item.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main content: Tickets & ISCIR */}
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        {/* LEFT: Tickets per equipment (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">Tichete deschise per echipament</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')} className="text-xs">
                  Vezi toate <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {data?.equipment_with_tickets?.length > 0 ? (
                <div className="space-y-3">
                  {data.equipment_with_tickets.map((eq, idx) => (
                    <div key={idx} className="border rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
                      <div className="flex flex-wrap items-center justify-between gap-1 px-3 lg:px-4 py-2 bg-slate-50/80">
                        <div className="flex items-center gap-2 min-w-0">
                          <Cog className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="font-semibold text-sm text-slate-800 truncate">{eq.equipment_name}</span>
                          <span className="text-xs text-slate-400 font-mono hidden sm:inline">{eq.equipment_code}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 font-semibold">
                            {eq.tickets.length} {eq.tickets.length === 1 ? 'tichet' : 'tichete'}
                          </Badge>
                          <div className="flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" />
                            {Math.round(eq.total_downtime_hours)}h
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {eq.tickets.map((t) => (
                          <div key={t.id} className="flex items-center justify-between px-3 lg:px-4 py-2 hover:bg-blue-50/30 cursor-pointer transition-colors" onClick={() => navigate('/tickets')}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_COLORS[t.priority_assigned] || PRIORITY_COLORS['N/A'] }} />
                              <span className="text-sm text-slate-700 truncate">{t.title}</span>
                            </div>
                            <span className="text-xs font-bold text-red-500 whitespace-nowrap shrink-0 ml-2">{t.downtime_display}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <TicketCheck className="w-8 h-8 text-green-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">Niciun tichet deschis</p>
                  <p className="text-xs text-slate-400 mt-1">Toate echipamentele functioneaza normal</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low stock */}
          {data?.low_stock?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Consumabile sub stoc minim</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/settings/consumables')} className="text-xs">
                    Vezi toate <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.low_stock.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-xl bg-orange-50/50 border border-orange-100 gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{item.name}</p>
                        <p className="text-xs text-slate-400">{item.code}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-orange-700">{item.current_stock} / {item.min_stock} {item.unit}</p>
                        <p className="text-[10px] text-red-500">lipsesc {item.deficit} {item.unit}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: ISCIR + Quick Nav */}
        <div className="space-y-6">
          {/* ISCIR */}
          {(data?.iscir_expired?.length > 0 || data?.iscir_expiring?.length > 0) && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-500" />
                    ISCIR
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/iscir')} className="text-xs">
                    Detalii <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.iscir_expired?.map((item, idx) => (
                    <div key={`exp-${idx}`} className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-red-50 border border-red-100">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{item.equipment_name}</p>
                        <p className="text-xs text-slate-400">Nr. {item.registry_number}</p>
                      </div>
                      <Badge className="bg-red-600 text-white text-[10px] shrink-0 ml-2">EXPIRAT</Badge>
                    </div>
                  ))}
                  {data.iscir_expiring?.map((item, idx) => (
                    <div key={`soon-${idx}`} className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-amber-50 border border-amber-100">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{item.equipment_name}</p>
                        <p className="text-xs text-slate-400">Nr. {item.registry_number}</p>
                      </div>
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] shrink-0 ml-2">
                        {item.days_left}z
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Priority breakdown */}
          {charts.priority_distribution?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-700">Prioritate tichete deschise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {charts.priority_distribution.map((item) => {
                    const total = c.open_tickets || 1;
                    const pct = Math.round((item.value / total) * 100);
                    return (
                      <div key={item.name} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[item.name] || '#94a3b8' }} />
                            <span className="font-medium text-slate-600 capitalize">{item.name}</span>
                          </div>
                          <span className="font-bold text-slate-700">{item.value}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: PRIORITY_COLORS[item.name] || '#94a3b8' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick navigation */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700">Acces rapid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Echipamente', icon: Cog, path: '/settings/equipment', color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
                  { label: 'Scule', icon: Wrench, path: '/tools', color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' },
                  { label: 'Tichete', icon: AlertTriangle, path: '/tickets', color: 'text-red-600 bg-red-50 hover:bg-red-100' },
                  { label: 'ISCIR', icon: Shield, path: '/iscir', color: 'text-amber-600 bg-amber-50 hover:bg-amber-100' },
                  { label: 'Consumabile', icon: Package, path: '/settings/consumables', color: 'text-orange-600 bg-orange-50 hover:bg-orange-100' },
                  { label: 'Echipamente ISCIR', icon: ClipboardList, path: '/iscir', color: 'text-violet-600 bg-violet-50 hover:bg-violet-100' },
                ].map(item => (
                  <button key={item.path} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors ${item.color}`} onClick={() => navigate(item.path)} data-testid={`quick-nav-${item.label.toLowerCase()}`}>
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboardPage;
